import React, { useState, useEffect, useRef } from 'react';
import { useTelegram } from '../hooks/useTelegram';
import { PaymentMethodSheet } from './PaymentMethodSheet';
import { Notification } from './Notification';
import { openPaymentUrl } from '../utils/paymentNavigation';
import '../App.css';

const ASSET_PATH = '/images';
const API_URL = window.location.origin;

// Константы для лимитов пополнения
const MIN_TOPUP_AMOUNT = 15;
const MAX_TOPUP_AMOUNT = 15000;

export function WalletPage({ onPageLoaded, internalUserId }) {
    const [balance, setBalance] = useState(0);
    const [amount, setAmount] = useState('150');
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMethod, setSelectedMethod] = useState({
    //     id: 'mulenpay',
    //     name: 'MulenPay',
    //     subtitle: 'Система быстрых платежей',
    //     icon: 'mulen.jpg',
    //     iconType: 'full',
    //     type: 'mulenpay'
    // });

        id: 'antilopay',
        name: 'AntiloPay',
        subtitle: 'Система быстрых платежей',
        icon: 'antilopay.jpg',
        iconType: 'full',
        type: 'antilopay'
     });
    const [showPaymentSheet, setShowPaymentSheet] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [notification, setNotification] = useState({
        isVisible: false,
        message: '',
        type: 'success'
    });
    const { userId, loading: tgLoading } = useTelegram();

    // Используем internalUserId из props, если он передан, иначе userId
    const effectiveUserId = internalUserId || userId;

    // Refs
    const amountContainerRef = useRef(null);
    const amountInputRef = useRef(null);
    const topupCardRef = useRef(null);
    const amountRefs = {
        150: useRef(null),
        300: useRef(null),
        450: useRef(null),
        900: useRef(null),
        other: useRef(null)
    };
    const [amountHighlightStyle, setAmountHighlightStyle] = useState({ left: 0, width: 0 });
    const [selectedQuickAmount, setSelectedQuickAmount] = useState(150);
    const [isCustomMode, setIsCustomMode] = useState(false);

    const quickAmounts = [150, 300, 450, 900];

    // Функция для проверки статуса оплаты
    const startPaymentCheck = (paymentId, system) => {
        let attempts = 0;
        const maxAttempts = 60; // 5 минут (каждые 5 секунд)

        const interval = setInterval(async () => {
            attempts++;
            try {
                const response = await fetch(`${API_URL}/api/payment/status/${paymentId}/${system}`);
                const data = await response.json();

                if (data.success && data.status === 'paid') {
                    clearInterval(interval);
                    showNotification('✅ Оплата прошла успешно! Баланс пополнен.', 'success');
                    // Обновляем данные
                    await Promise.all([
                        fetchBalance(),
                        fetchTransactions()
                    ]);
                } else if (attempts >= maxAttempts) {
                    clearInterval(interval);
                    showNotification('⏰ Время ожидания оплаты истекло', 'warning');
                }
            } catch (error) {
                console.error('Ошибка проверки статуса:', error);
            }
        }, 5000); // Проверяем каждые 5 секунд
    };

    // Загрузка данных пользователя
    useEffect(() => {
        console.log('🔄 WalletPage: useEffect triggered, effectiveUserId:', effectiveUserId, 'tgLoading:', tgLoading);

        const timeoutId = setTimeout(() => {
            console.log('⚠️ WalletPage: Таймаут загрузки, принудительно завершаем');
            setLoading(false);
            if (onPageLoaded) {
                onPageLoaded();
            }
        }, 5000);

        if (effectiveUserId && !tgLoading) {
            console.log('📡 WalletPage: Начинаем загрузку данных...');

            Promise.all([
                fetchBalance(),
                fetchTransactions()
            ]).then(() => {
                console.log('✅ WalletPage: Все данные загружены');
                setLoading(false);
                clearTimeout(timeoutId);
                if (onPageLoaded) {
                    console.log('📢 WalletPage: Вызываем onPageLoaded');
                    onPageLoaded();
                }
            }).catch((error) => {
                console.error('❌ WalletPage: Ошибка загрузки данных:', error);
                setLoading(false);
                clearTimeout(timeoutId);
                if (onPageLoaded) {
                    onPageLoaded();
                }
            });
        } else {
            console.log('⏳ WalletPage: Ожидание effectiveUserId или tgLoading...');
        }

        return () => clearTimeout(timeoutId);
    }, [effectiveUserId, tgLoading]);

    // Автоматическое обновление каждые 30 секунд
    useEffect(() => {
        if (!loading && effectiveUserId) {
            const interval = setInterval(() => {
                if (document.visibilityState === 'visible') {
                    console.log('🔄 Автоматическое обновление баланса...');
                    fetchBalance();
                    fetchTransactions();
                }
            }, 10000); // Каждые 10 секунд

            return () => clearInterval(interval);
        }
    }, [loading, effectiveUserId]);

    useEffect(() => {
        const refreshWallet = () => {
            if (effectiveUserId) {
                fetchBalance();
                fetchTransactions();
            }
        };
        const applyBalance = event => {
            if (event.detail?.balance !== undefined) setBalance(event.detail.balance);
            if (effectiveUserId) fetchTransactions();
        };

        window.addEventListener('hydvpn-data-updated', refreshWallet);
        window.addEventListener('balance-updated', applyBalance);
        return () => {
            window.removeEventListener('hydvpn-data-updated', refreshWallet);
            window.removeEventListener('balance-updated', applyBalance);
        };
    }, [effectiveUserId]);

    // Обновление позиции хайлайта
    useEffect(() => {
        if (!loading && amountContainerRef.current) {
            let activeRef = null;
            if (isCustomMode) {
                activeRef = amountRefs.other;
            } else {
                activeRef = amountRefs[selectedQuickAmount];
            }
            if (activeRef?.current) {
                const containerRect = amountContainerRef.current.getBoundingClientRect();
                const activeRect = activeRef.current.getBoundingClientRect();
                setAmountHighlightStyle({
                    left: activeRect.left - containerRect.left,
                    width: activeRect.width
                });
            }
        }
    }, [selectedQuickAmount, isCustomMode, loading]);

    // Установка начальной позиции хайлайта
    useEffect(() => {
        if (!loading && amountContainerRef.current && amountRefs[150]?.current) {
            const containerRect = amountContainerRef.current.getBoundingClientRect();
            const activeRect = amountRefs[150].current.getBoundingClientRect();
            setAmountHighlightStyle({
                left: activeRect.left - containerRect.left,
                width: activeRect.width
            });
        }
    }, [loading]);

    // Получение баланса пользователя
    const fetchBalance = async () => {
        try {
            console.log('💰 WalletPage: Запрос баланса...');
            const response = await fetch(`${API_URL}/api/user/${effectiveUserId}/balance`);
            const data = await response.json();
            if (data.success) {
                setBalance(data.balance);
                console.log('💰 WalletPage: Баланс получен:', data.balance);
            }
        } catch (error) {
            console.error('❌ Ошибка загрузки баланса:', error);
            throw error;
        }
    };

    // Получение истории транзакций
    const fetchTransactions = async () => {
        try {
            console.log('📜 WalletPage: Запрос транзакций...');
            const response = await fetch(`${API_URL}/api/user/${effectiveUserId}/transactions`);
            const data = await response.json();
            if (data.success) {
                setTransactions(data.transactions || []);
                console.log('📜 WalletPage: Транзакций получено:', data.transactions?.length || 0);
            }
        } catch (error) {
            console.error('❌ Ошибка загрузки транзакций:', error);
            throw error;
        }
    };

    // Выбор суммы из быстрых вариантов
    const handleQuickAmountSelect = (amount) => {
        setSelectedQuickAmount(amount);
        setIsCustomMode(false);
        setAmount(amount.toString());
        if (amountInputRef.current) {
            amountInputRef.current.blur();
        }
    };

    // Выбор "Другое"
    const handleOtherSelect = () => {
        setIsCustomMode(true);
        setSelectedQuickAmount(null);
        setAmount('');

        setTimeout(() => {
            if (amountInputRef.current) {
                amountInputRef.current.focus();
            }
        }, 100);
    };

    // Изменение суммы в инпуте
    const handleAmountChange = (e) => {
        const rawValue = e.target.value;
        let value = rawValue.replace(/[^0-9]/g, '');

        if (value.length > 1 && value.startsWith('0')) {
            value = value.replace(/^0+/, '');
        }

        if (value !== '') {
            const numValue = parseInt(value);
            if (numValue > MAX_TOPUP_AMOUNT) {
                value = MAX_TOPUP_AMOUNT.toString();
            }
        }

        setAmount(value);

        if (value && value !== '') {
            const matchedQuick = quickAmounts.find(q => q.toString() === value);
            if (matchedQuick) {
                setSelectedQuickAmount(matchedQuick);
                setIsCustomMode(false);
            } else {
                setIsCustomMode(true);
                setSelectedQuickAmount(null);
            }
        } else if (value === '') {
            setIsCustomMode(true);
            setSelectedQuickAmount(null);
        }
    };

    // Получение итоговой суммы для отправки
    const getFinalAmount = () => {
        if (!amount || amount === '') {
            return 0;
        }
        const numAmount = parseInt(amount);
        return isNaN(numAmount) ? 0 : numAmount;
    };

    // Обработка выбора метода оплаты
    const handleMethodSelect = (method) => {
        setSelectedMethod(method);
        console.log('Выбран метод оплаты:', method);
    };

    // Валидация суммы
    const validateAmount = (amount) => {
        if (amount <= 0) {
            showNotification('Введите корректную сумму', 'error');
            return false;
        }
        if (amount < MIN_TOPUP_AMOUNT) {
            showNotification(`Минимальная сумма пополнения ${MIN_TOPUP_AMOUNT} рублей`, 'error');
            return false;
        }
        if (amount > MAX_TOPUP_AMOUNT) {
            showNotification(`Максимальная сумма пополнения ${MAX_TOPUP_AMOUNT} рублей`, 'error');
            return false;
        }
        return true;
    };

    const openPaymentPage = async paymentUrl => {
        try {
            return await openPaymentUrl(paymentUrl);
        } catch (openError) {
            console.error('Не удалось открыть страницу оплаты:', openError);
            showNotification(openError.message || 'Не удалось открыть страницу оплаты', 'error');
            return null;
        }
    };

    // Обработка пополнения
    const handleTopUp = async () => {
        const finalAmount = getFinalAmount();

        if (!validateAmount(finalAmount)) {
            return;
        }

        setProcessing(true);

        try {
            let endpoint = '';
            let successMessage = '';

            switch (selectedMethod.id) {
                case 'cryptobot':
                    endpoint = `${API_URL}/api/crypto/create-invoice`;
                    successMessage = 'Счет в CryptoBot создан!';
                    break;
                case 'heleket':
                    endpoint = `${API_URL}/api/heleket/create-invoice`;
                    successMessage = 'Счет в Heleket создан!';
                    break;
                case 'mulenpay':
                    endpoint = `${API_URL}/api/mulenpay/create-invoice`;
                    successMessage = 'Счет в MulenPay создан!';
                    break;
                case 'antilopay':
                    endpoint = `${API_URL}/api/antilopay/create-invoice`;
                    successMessage = 'Счет в Antilopay создан!';
                    break;
                case 'lavapay':
                    endpoint = `${API_URL}/api/lavapay/create-invoice`;
                    successMessage = 'Счет в LavaPay создан!';
                    break;
                default:
                    showNotification('Неизвестный способ оплаты', 'error');
                    setProcessing(false);
                    return;
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: effectiveUserId,  // ← используем effectiveUserId
                    amount: finalAmount
                })
            });

            const data = await response.json();

            if (data.success) {
                showNotification(successMessage, 'success');

                const payUrl = data.payUrl || data.paymentUrl;
                const paymentId = data.invoice?.id || data.invoice?.order_id || data.invoice?.invoice_id;
                const system = selectedMethod.id;

                if (!payUrl) {
                    throw new Error('Платёжная система не вернула ссылку на оплату');
                }

                const paymentPageOpened = await openPaymentPage(payUrl);

                // Запускаем проверку статуса оплаты
                if (paymentPageOpened === 'telegram' && paymentId) {
                    startPaymentCheck(paymentId, system);
                }
            } else {
                showNotification(data.error || 'Ошибка при создании счета', 'error');
            }
        } catch (error) {
            console.error('Ошибка при пополнении:', error);
            showNotification(error.message || 'Ошибка при пополнении', 'error');
        } finally {
            setProcessing(false);
        }
    };

    // Показать уведомление
    const showNotification = (message, type = 'success') => {
        setNotification({ isVisible: true, message, type });
    };

    // Скрыть уведомление
    const hideNotification = () => {
        setNotification(prev => ({ ...prev, isVisible: false }));
    };

    // Форматирование даты
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Форматирование суммы
    const formatAmount = (amount, type) => {
        const sign = (type === 'topup' || type === 'referral_withdrawal') ? '+' : '-';
        return `${sign}${Math.abs(amount).toFixed(2)}₽`;
    };

    console.log('🎨 WalletPage: Рендер, loading =', loading);

    if (loading) {
        console.log('⏳ WalletPage: Показываем null (загрузка)');
        return null;
    }

    const displayAmount = amount || '';
    const finalAmount = getFinalAmount();
    const isAmountValid = finalAmount >= MIN_TOPUP_AMOUNT && finalAmount <= MAX_TOPUP_AMOUNT;
    const isAmountTooLow = finalAmount > 0 && finalAmount < MIN_TOPUP_AMOUNT;
    const isAmountTooHigh = finalAmount > MAX_TOPUP_AMOUNT;

    // Разделяем целую часть и копейки
    const balanceStr = balance.toFixed(2);
    const [integerPart, decimalPart] = balanceStr.split('.');

    return (
        <div className="wallet-page page-enter-animate" style={(showPaymentSheet ? {overflowY: 'hidden'} : {overflowX: 'auto'})}>
            <div className="content-wrapper">
                {/* Баланс */}
                <div className="balance-container">
                    <div className="balance-label">Баланс кошелька</div>
                    <div className="balance-amount" style={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                        <span style={{fontSize: '32px', fontWeight: '600' }}>₽</span>
                        <span style={{ color: '#ffffff', fontSize: '32px', fontWeight: '600' }}>{integerPart}</span>
                        <span style={{fontSize: '32px', fontWeight: '600' }}>.{decimalPart}</span>
                    </div>
                </div>

                {/* Форма пополнения */}
                <div className="form-stack">
                    {/* Способ пополнения */}
                    <div
                        className="glass-card method-selector"
                        onClick={() => setShowPaymentSheet(true)}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className="method-left">
                            <div className="method-icon-box">
                                <img
                                    src={`${ASSET_PATH}/${selectedMethod.icon}`}
                                    alt={selectedMethod.name}
                                    className="method-icon"
                                />
                            </div>
                            <div className="method-info">
                                <span className="method-label">Способ пополнения</span>
                                <span className="method-value">{selectedMethod.subtitle}</span>
                            </div>
                        </div>
                        <div className="chevron-box">
                            <img
                                src={`${ASSET_PATH}/0_97.svg`}
                                alt="Chevron"
                                className="chevron-icon"
                            />
                        </div>
                    </div>

                    {/* Карточка пополнения */}
                    <div className="key-card topup-card" ref={topupCardRef}>
                        <div className="card-content">
                            <div className="input-display">
                                <span className="input-label">Сумма пополнения</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginTop: '4px' }}>
                                    <span style={{
                                        fontSize: '28px',
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        lineHeight: '1',
                                        display: 'inline-block'
                                    }}>₽</span>
                                    <input
                                        ref={amountInputRef}
                                        type="tel"
                                        inputMode="numeric"
                                        value={displayAmount}
                                        placeholder="0"
                                        onChange={handleAmountChange}
                                        style={{
                                            fontSize: '28px',
                                            fontWeight: '700',
                                            color: 'white',
                                            background: 'transparent',
                                            border: 'none',
                                            outline: 'none',
                                            padding: 0,
                                            margin: 0,
                                            fontFamily: 'inherit',
                                            width: `${Math.max(60, String(displayAmount || '0').length * 20)}px`,
                                            minWidth: '40px',
                                            lineHeight: '1',
                                            display: 'inline-block'
                                        }}
                                    />
                                </div>

                                {/* Сообщение об ошибке валидации */}
                                {finalAmount > 0 && (
                                    <>
                                        {isAmountTooLow && (
                                            <div className="amount-error" style={{ marginTop: '4px', fontSize: '12px', color: '#ff5555' }}>
                                                Минимальная сумма: {MIN_TOPUP_AMOUNT} ₽
                                            </div>
                                        )}
                                        {isAmountTooHigh && (
                                            <div className="amount-error" style={{ marginTop: '4px', fontSize: '12px', color: '#ff5555' }}>
                                                Максимальная сумма: {MAX_TOPUP_AMOUNT} ₽
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Переключатель сумм */}
                            <div className="amount-selector-wide" ref={amountContainerRef}>
                                <div
                                    className="amount-highlight-wide"
                                    style={{ left: amountHighlightStyle.left, width: amountHighlightStyle.width }}
                                />
                                {quickAmounts.map(quickAmount => (
                                    <div
                                        key={quickAmount}
                                        className={`amount-item-wide ${!isCustomMode && selectedQuickAmount === quickAmount ? 'active' : ''}`}
                                        ref={amountRefs[quickAmount]}
                                        onClick={() => handleQuickAmountSelect(quickAmount)}
                                        onMouseDown={(e) => e.preventDefault()}
                                    >
                                        <span>{quickAmount}₽</span>
                                    </div>
                                ))}
                                <div
                                    className={`amount-item-wide ${isCustomMode ? 'active' : ''}`}
                                    ref={amountRefs.other}
                                    onClick={handleOtherSelect}
                                    onMouseDown={(e) => e.preventDefault()}
                                >
                                    <span>Другое</span>
                                </div>
                            </div>

                            <button
                                className={`btn ${isAmountValid ? 'btn-green' : 'btn-disabled'}`}
                                onClick={handleTopUp}
                                disabled={processing || !isAmountValid}
                            >
                                <span className="btn-text">
                                    {processing ? 'Создание счета...' : 'Перейти к пополнению'}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* История операций */}
            <div className="history-wrapper">
                <h3 className="history-title">История операций:</h3>

                {transactions.length === 0 ? (
                    <div className="glass-card empty-state">
                        <div className="empty-content">
                            <div className="empty-title">У вас пока нет транзакций</div>
                            <div className="empty-desc">
                                История операций появится после<br />первых транзакций.
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="history-list page-enter-animate">
                        {transactions.map((transaction, index) => (
                            <div key={index} className="history-item">
                                <div className="item-left">
                                    <img
                                        src={`${ASSET_PATH}/0_123.svg`}
                                        className="item-icon"
                                        alt="Transaction"
                                    />
                                    <div className="item-details">
                                        <span className="item-date">{formatDate(transaction.created_at)}</span>
                                        <span className="item-desc">{transaction.description}</span>
                                    </div>
                                </div>
                                <div className="item-right">
                                    <span className="item-amount">
                                        {formatAmount(transaction.amount, transaction.type)}
                                    </span>
                                    <div className="item-status">
                                        <img
                                            src={`${ASSET_PATH}/0_130.svg`}
                                            className="status-icon"
                                            alt="Status"
                                        />
                                        <span className="status-text">
                                            {transaction.status === 'completed' ? 'Оплачено' : transaction.status}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Шторка выбора способа оплаты */}
            <PaymentMethodSheet
                isVisible={showPaymentSheet}
                onClose={() => setShowPaymentSheet(false)}
                onSelect={handleMethodSelect}
            />

            {/* Уведомления */}
            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
                duration={3000}
            />
        </div>
    );
}
